import { Fragment, useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api";
import { FiX, FiCalendar, FiSettings } from "react-icons/fi";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/shadcn/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/dropdown-menu";
import { Switch } from "@/components/ui/shadcn/switch";
import Button from "@/components/ui/Button";
import useFileUpload from "@/hooks/useFileUpload";
import { useModal } from "@/contexts/ModalContext";
import AssetsBrowserModal from "@/components/modals/AssetsBrowserModal";
import { LuAudioLines, LuLoaderCircle } from "react-icons/lu";
import {
  FiPlus,
  FiUpload,
  FiTrash2,
  FiPause,
  FiPlay,
  FiChevronUp,
  FiChevronDown,
} from "react-icons/fi";
import { TbReplace } from "react-icons/tb";
// Image component - use native img tag in Vite build
import useAudioController from "@/hooks/useAudioController";
import { useShow } from "@/contexts/ShowContext";
import ScheduleModal from "@/components/modals/ScheduleModal";
import VoiceSettingsModal from "@/components/modals/VoiceSettingsModal";
import { Checkbox } from "@/components/ui/shadcn/checkbox";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/shadcn/command";
import { Input } from "@/components/ui/shadcn/input";
import { GENERATION_DRAG_TYPE } from "@/components/storyboard/StoryboardGenerations";

export const GENERATION_MEDIA_PREFIX = "application/x-kagami-gen-";
export function generationMediaDragType(mediaType) {
  return `${GENERATION_MEDIA_PREFIX}${mediaType}`;
}
function getDraggedMediaType(dataTransferTypes) {
  const match = dataTransferTypes.find((t) =>
    t.startsWith(GENERATION_MEDIA_PREFIX),
  );
  return match ? match.slice(GENERATION_MEDIA_PREFIX.length) : null;
}
function hasMatchingFileItem(dataTransfer, mediaType) {
  const items = [...(dataTransfer?.items || [])];
  if (items.length === 0) return true;
  return items.some(
    (item) =>
      item.kind === "file" &&
      (!item.type || item.type.startsWith(`${mediaType}/`)),
  );
}

export function ModelSelectorDialog({
  open,
  onOpenChange,
  models,
  selectedModel,
  onSelectModel,
  hasCategoryFilter,
}) {
  const [modelTypes, setModelTypes] = useState({
    t2i: { value: true, label: "Text 2 Image" },
    i2v: { value: true, label: "Image 2 Video" },
    v2v: { value: true, label: "Video 2 Video" },
    t2s: { value: true, label: "Text 2 Speech" },
    utils: { value: true, label: "Utils" },
  });

  return (
    <CommandDialog className="h-3/4" open={open} onOpenChange={onOpenChange}>
      <Command
        className="ModelSelector bg-card"
        value={selectedModel?.endpoint}
        filter={(_value, search, keywords) => {
          const haystack = (keywords || []).join(" ").toLowerCase();
          return haystack.includes(search.trim().toLowerCase()) ? 1 : 0;
        }}>
        <CommandInput
          className="ModelSelector__search"
          placeholder="Search for a model..."
        />
        {!hasCategoryFilter && (
          <div className="flex items-center gap-3 p-4 border-b">
            {Object.keys(modelTypes).map((key) => (
              <div className="flex items-center gap-2" key={key}>
                <Checkbox
                  id={`ModelSelector__filter-${key}`}
                  name={`filter-${key}`}
                  checked={modelTypes[key]?.value}
                  onCheckedChange={(checked) =>
                    setModelTypes((prev) => ({
                      ...prev,
                      [key]: { ...prev[key], value: checked },
                    }))
                  }
                />
                <label
                  className="text-[10px] text-secondary-foreground/80 hover:text-secondary-foreground capitalize select-none capitalize  cursor-pointer"
                  onClick={() =>
                    setModelTypes((prev) =>
                      Object.fromEntries(
                        Object.entries(prev).map(([k, v]) => [
                          k,
                          { ...v, value: k === key },
                        ]),
                      ),
                    )
                  }>
                  {modelTypes[key]?.label}
                </label>
              </div>
            ))}
          </div>
        )}
        <CommandList className="max-h-full">
          <CommandEmpty className="text-xs text-secondary-foreground/80 p-5 text-center">
            No models found.
          </CommandEmpty>
          {Object.entries(
            models
              .filter((model) => modelTypes[model.category]?.value)
              .reduce((groups, model) => {
                const group = model.group || "Other";
                if (!groups[group]) groups[group] = [];
                groups[group].push(model);
                return groups;
              }, {}),
          )
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([groupName, groupModels]) => (
              <Fragment key={groupName}>
                <CommandGroup heading={groupName}>
                  {groupModels
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((model) => (
                      <CommandItem
                        value={model.endpoint}
                        keywords={[model.name, model.group].filter(Boolean)}
                        key={model._id}
                        className="ModelSelector__model px-4 py-2 flex flex-col gap-1 items-start"
                        onSelect={() => {
                          onSelectModel(model);
                          onOpenChange(false);
                        }}>
                        <div className="flex items-center justify-between w-full">
                          <span>{model.name}</span>
                          <span className="text-[10px]">
                            {modelTypes[model.category]?.label}
                          </span>
                        </div>
                        <span className="text-[10px] opacity-60">
                          {model.description}
                        </span>
                      </CommandItem>
                    ))}
                </CommandGroup>
                <CommandSeparator />
              </Fragment>
            ))}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}

export function AssetPicker({
  className,
  onSelect,
  preview,
  onRemove,
  canRemove = true,
  onAdd,
  onChange,
  onReplace,
  canReplace = true,
  mediaType = "image",
  allowAssetBrowser = true,
  allowUpload = true,
  allowPreview = true,
  asset = null,
  multiSelect = false,
  onMoveUp,
  onMoveDown,
}) {
  const { openModal } = useModal();
  const { uploadFile, uploadProgress, uploadError, isUploading } =
    useFileUpload();
  const [uid, setUid] = useState(Math.random().toString(36).substring(2, 15));
  const [multiUploadStatus, setMultiUploadStatus] = useState(null);
  const {
    playAudio,
    stopAudio,
    isAudioPlaying,
    currentAudio,
    audioPlaybackProgress,
    playAudioAsync,
  } = useAudioController();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isDragTypeMatch, setIsDragTypeMatch] = useState(false);
  const dragCounterRef = useCallback(() => ({ current: 0 }), [])();
  const dragStateRef = useCallback(
    () => ({ active: false, match: false }),
    [],
  )();

  useEffect(() => {
    let scheduled = false;
    let dragActiveTimeout = null;
    const resetDragState = () => {
      dragStateRef.active = false;
      dragStateRef.match = false;
      setIsDragActive(false);
      setIsDragTypeMatch(false);
      setIsDragOver(false);
      dragCounterRef.current = 0;
      if (dragActiveTimeout) {
        clearTimeout(dragActiveTimeout);
        dragActiveTimeout = null;
      }
    };
    const handleGlobalDragOver = (e) => {
      const types = [...e.dataTransfer.types];
      const isGenDrag = types.includes(GENERATION_DRAG_TYPE);
      const isFileDrag = types.includes("Files");
      if (!isGenDrag && !(isFileDrag && allowUpload)) return;
      let nowMatch;
      if (isGenDrag) {
        const draggedType = getDraggedMediaType(types);
        nowMatch = draggedType === mediaType;
      } else {
        nowMatch = hasMatchingFileItem(e.dataTransfer, mediaType);
      }
      if (dragActiveTimeout) clearTimeout(dragActiveTimeout);
      dragActiveTimeout = setTimeout(resetDragState, 300);
      if (true === dragStateRef.active && nowMatch === dragStateRef.match)
        return;
      dragStateRef.active = true;
      dragStateRef.match = nowMatch;
      if (!scheduled) {
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          setIsDragActive(dragStateRef.active);
          setIsDragTypeMatch(dragStateRef.match);
        });
      }
    };
    document.addEventListener("dragover", handleGlobalDragOver);
    document.addEventListener("dragend", resetDragState);
    document.addEventListener("drop", resetDragState);
    return () => {
      document.removeEventListener("dragover", handleGlobalDragOver);
      document.removeEventListener("dragend", resetDragState);
      document.removeEventListener("drop", resetDragState);
      if (dragActiveTimeout) clearTimeout(dragActiveTimeout);
    };
  }, [mediaType, allowUpload]);

  const handleDragOver = useCallback(
    (e) => {
      const types = [...e.dataTransfer.types];
      const isGenDrag = types.includes(GENERATION_DRAG_TYPE);
      const isFileDrag = types.includes("Files");
      if (!isGenDrag && !(isFileDrag && allowUpload)) return;
      if (isGenDrag) {
        const draggedType = getDraggedMediaType(types);
        if (draggedType !== mediaType) {
          e.dataTransfer.dropEffect = "none";
          return;
        }
      } else if (!hasMatchingFileItem(e.dataTransfer, mediaType)) {
        e.dataTransfer.dropEffect = "none";
        return;
      }
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    },
    [mediaType, allowUpload],
  );

  const handleDragEnter = useCallback(
    (e) => {
      const types = [...e.dataTransfer.types];
      const isGenDrag = types.includes(GENERATION_DRAG_TYPE);
      const isFileDrag = types.includes("Files");
      if (!isGenDrag && !(isFileDrag && allowUpload)) return;
      if (isGenDrag) {
        const draggedType = getDraggedMediaType(types);
        if (draggedType !== mediaType) return;
      } else if (!hasMatchingFileItem(e.dataTransfer, mediaType)) {
        return;
      }
      e.preventDefault();
      dragCounterRef.current++;
      setIsDragOver(true);
    },
    [mediaType, allowUpload],
  );

  const handleDragLeave = useCallback((e) => {
    const types = [...e.dataTransfer.types];
    if (!types.includes(GENERATION_DRAG_TYPE) && !types.includes("Files"))
      return;
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    }
  }, []);

  const uploadFiles = useCallback(
    async (files) => {
      const valid = files.filter((f) =>
        (f?.type || "").startsWith(`${mediaType}/`),
      );
      if (valid.length === 0) return;
      if (multiSelect) {
        const uploadResponses = [];
        try {
          for (let i = 0; i < valid.length; i++) {
            setMultiUploadStatus({ current: i + 1, total: valid.length });
            const uploadResponse = await uploadFile(valid[i], "temp");
            if (uploadResponse) uploadResponses.push(uploadResponse);
          }
        } finally {
          setMultiUploadStatus(null);
        }
        if (uploadResponses.length === 0) return;
        onAdd?.(uploadResponses);
        onChange?.(uploadResponses);
      } else {
        const uploadResponse = await uploadFile(valid[0], "temp");
        if (!uploadResponse) return;
        onAdd?.(uploadResponse.url);
        onChange?.(uploadResponse.url);
      }
    },
    [mediaType, multiSelect, uploadFile, onAdd, onChange],
  );

  const handleDrop = useCallback(
    async (e) => {
      const types = [...e.dataTransfer.types];
      const isGenDrag = types.includes(GENERATION_DRAG_TYPE);
      const isFileDrag = types.includes("Files");
      if (!isGenDrag && !(isFileDrag && allowUpload)) return;
      e.preventDefault();
      setIsDragOver(false);
      setIsDragActive(false);
      setIsDragTypeMatch(false);
      dragCounterRef.current = 0;
      if (isGenDrag) {
        const raw = e.dataTransfer.getData(GENERATION_DRAG_TYPE);
        if (!raw) return;
        let payload;
        try {
          payload = JSON.parse(raw);
        } catch {
          return;
        }
        const { url, type } = payload;
        if (!url || type !== mediaType) return;
        onAdd?.(url);
        onChange?.(url);
        return;
      }
      const files = Array.from(e.dataTransfer.files || []);
      if (files.length === 0) return;
      await uploadFiles(multiSelect ? files : files.slice(0, 1));
    },
    [mediaType, allowUpload, multiSelect, onAdd, onChange, uploadFiles],
  );

  const handleAddAsset = async () => {
    const result = await openModal(
      <AssetsBrowserModal mediaType={mediaType} multiSelect={multiSelect} />,
    );
    if (result) {
      if (multiSelect && Array.isArray(result)) {
        if (onAdd) onAdd(result);
        if (onChange) onChange(result);
      } else {
        if (onAdd) onAdd(result.url);
        if (onChange) onChange(result.url);
      }
    }
  };

  const handleClearAsset = () => {
    const url = asset;
    if (onRemove) onRemove(url);
    if (onChange) onChange(null);
  };

  const handleSelect = () => {
    if (onSelect) onSelect({ url: asset, type: mediaType });
  };

  const handleReplaceAsset = () => {
    if (onReplace) onReplace({ url: asset, type: mediaType });
  };

  const handleUploadAsset = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    await uploadFiles(files);
  };

  const resolvePreviewUrl = async () => {
    if (!preview?.url) return null;
    return typeof preview.url === "function"
      ? await preview.url()
      : preview.url;
  };

  const handleFromPreview = async () => {
    const url = await resolvePreviewUrl();
    if (!url) return;
    onAdd?.(url);
    onChange?.(url);
  };

  const handleAudioPreview = () => {
    if (isAudioPlaying && currentAudio === asset) {
      stopAudio();
    } else {
      playAudio(asset);
    }
  };

  return (
    <div
      className={`@container relative border border-secondary bg-background rounded-md overflow-hidden cursor-pointer flex ${asset ? "border-solid" : "border-dashed"} ${isDragOver ? "border-primary border-solid bg-primary/10 ring-1 ring-primary/30" : ""} ${isDragActive && !isDragTypeMatch ? "opacity-30" : ""} ${className}`}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      {allowUpload && (
        <input
          id={`upload-asset-${uid}`}
          type="file"
          accept={`${mediaType}/*`}
          multiple={multiSelect}
          onChange={handleUploadAsset}
          className="hidden"
        />
      )}
      {asset && allowPreview && (
        <div className="flex flex-1 relative group" onClick={handleSelect}>
          {mediaType === "image" && (
            <img
              src={asset}
              alt="asset preview"
              loading="lazy"
              decoding="async"
              className="object-cover h-full w-full"
            />
          )}
          {mediaType === "video" && (
            <video
              src={asset}
              muted
              controls={false}
              className="w-full h-full object-cover"
            />
          )}
          {mediaType === "audio" && (
            <div
              className="min-h-14 flex flex-1 justify-center items-center text-secondary-foreground/80 cursor-pointer hover:text-secondary-foreground group/audio-preview"
              onClick={handleAudioPreview}>
              <LuAudioLines
                className={`w-8 h-8 block group-hover/audio-preview:hidden ${isAudioPlaying && currentAudio === asset ? "animate-pulse" : ""}`}
              />
              {isAudioPlaying && currentAudio === asset ? (
                <FiPause
                  className={`w-6 h-6 text-secondary-foreground/80 hidden group-hover/audio-preview:block`}
                />
              ) : (
                <FiPlay
                  className={`w-6 h-6 text-secondary-foreground/80 hidden group-hover/audio-preview:block`}
                />
              )}
            </div>
          )}
          {(onMoveUp !== undefined || onMoveDown !== undefined) && (
            <div className="absolute top-[1px] left-[1px] flex hidden bg-card/90 rounded-md group-hover:flex">
              <button
                type="button"
                disabled={!onMoveUp}
                className="flex items-center justify-center text-secondary-foreground/80 cursor-pointer p-[4px] hover:text-secondary-foreground disabled:opacity-40 disabled:hover:text-secondary-foreground/80"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveUp?.();
                }}>
                <FiChevronUp className="w-3 h-3 @[150px]:w-4 @[150px]:h-4" />
              </button>
              <button
                type="button"
                disabled={!onMoveDown}
                className="flex items-center justify-center text-secondary-foreground/80 cursor-pointer p-[4px] hover:text-secondary-foreground disabled:opacity-40 disabled:hover:text-secondary-foreground/80"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveDown?.();
                }}>
                <FiChevronDown className="w-3 h-3 @[150px]:w-4 @[150px]:h-4" />
              </button>
            </div>
          )}
          <div className="absolute top-[1px] right-[1px] flex hidden bg-card/90 rounded-md group-hover:flex">
            {canReplace && (
              <div
                className="flex items-center justify-center text-secondary-foreground/80 cursor-pointer p-[4px] hover:text-secondary-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReplaceAsset();
                }}>
                <TbReplace className="w-3 h-3 @[150px]:w-4 @[150px]:h-4" />
              </div>
            )}
            {allowUpload && (
              <label
                htmlFor={`upload-asset-${uid}`}
                className="flex items-center justify-center text-secondary-foreground/80 cursor-pointer p-[4px] hover:text-secondary-foreground"
                onClick={(e) => e.stopPropagation()}>
                <FiUpload className="w-3 h-3 @[150px]:w-4 @[150px]:h-4" />
              </label>
            )}
            {allowAssetBrowser && (
              <div
                className="flex items-center justify-center text-secondary-foreground/80 cursor-pointer p-[4px] hover:text-secondary-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddAsset();
                }}>
                <FiPlus className="w-3 h-3 @[150px]:w-4 @[150px]:h-4" />
              </div>
            )}
            {canRemove && (
              <div
                className="flex items-center justify-center text-destructive-foreground/80 cursor-pointer p-[4px] hover:text-destructive-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAsset();
                }}>
                <FiTrash2 className="w-3 h-3 @[150px]:w-4 @[150px]:h-4" />
              </div>
            )}
          </div>
        </div>
      )}
      {isUploading && (
        <div
          className={`flex flex-1 min-h-14 items-center justify-center gap-2 p-2 text-secondary-foreground/60 ${asset ? "absolute inset-0 bg-background/60 z-10" : ""}`}>
          <LuLoaderCircle className="w-3 h-3 animate-spin" />
          <span className="text-[10px] hidden @[150px]:inline">
            Uploading
            {multiUploadStatus
              ? ` ${multiUploadStatus.current}/${multiUploadStatus.total}`
              : ""}
            {uploadProgress ? ` · ${uploadProgress}%` : "..."}
          </span>
        </div>
      )}
      {!asset && !isUploading && (
        <>
          <div className="flex justify-center items-center flex-1 hidden @[150px]:flex @[150px]:p-4 ">
            {allowUpload && (
              <label
                htmlFor={`upload-asset-${uid}`}
                className="flex p-2 text-secondary-foreground cursor-pointer rounded-sm hover:bg-primary hover:text-primary-foreground">
                <div className="flex items-center gap-1 cursor-pointer">
                  <FiUpload className="w-3 h-3 shrink-0" />
                  <span className="text-[10px] hidden @[150px]:inline">
                    Upload
                  </span>
                </div>
              </label>
            )}
            {allowAssetBrowser && (
              <div
                className="flex items-center gap-1 p-2 text-secondary-foreground cursor-pointer rounded-sm hover:bg-primary hover:text-primary-foreground"
                onClick={handleAddAsset}>
                <FiPlus className="w-3 h-3 shrink-0" />
                <span className="text-[10px] hidden @[150px]:inline">
                  Browse
                </span>
              </div>
            )}
            {(typeof preview?.url === "function" || preview?.url !== asset) &&
              preview?.type === mediaType && (
                <div
                  className="flex rounded-sm items-center gap-1 p-2 text-secondary-foreground cursor-pointer hover:bg-primary hover:text-primary-foreground"
                  onClick={handleFromPreview}>
                  <FiPlus className="w-3 h-3 shrink-0" />
                  <span className="text-[10px] hidden @[150px]:inline">
                    From Preview
                  </span>
                </div>
              )}
          </div>
          <div className="flex justify-center items-center flex-1 @[150px]:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center gap-1 p-2 text-secondary-foreground cursor-pointer hover:text-secondary-foreground/80">
                  <FiPlus className="w-4 h-4" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                {allowUpload && (
                  <DropdownMenuItem
                    className="text-xs gap-2"
                    onClick={() =>
                      document.getElementById(`upload-asset-${uid}`)?.click()
                    }>
                    <FiUpload className="w-3 h-3" /> Upload
                  </DropdownMenuItem>
                )}
                {allowAssetBrowser && (
                  <DropdownMenuItem
                    className="text-xs gap-2"
                    onClick={handleAddAsset}>
                    <FiPlus className="w-3 h-3" /> Browse
                  </DropdownMenuItem>
                )}
                {(typeof preview?.url === "function" ||
                  preview?.url !== asset) &&
                  preview?.type === mediaType && (
                    <DropdownMenuItem
                      className="text-xs gap-2"
                      onClick={handleFromPreview}>
                      <FiPlus className="w-3 h-3" /> From Preview
                    </DropdownMenuItem>
                  )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </>
      )}
    </div>
  );
}

export default function AiModelEditor({
  selectedModel,
  modelData,
  onModelDataChange,
  preview,
  onPreviewChange,
  onSubmit,
  onKeyDown,
}) {
  const [totalGenerations, setTotalGenerations] = useState(1);
  const [characters, setCharacters] = useState([]);
  const { openModal } = useModal();
  const { show } = useShow();

  const hasCharactersInput = selectedModel?.inputs?.some(
    (input) => input.type === "character",
  );

  useEffect(() => {
    if (!hasCharactersInput || !show?._id) return;
    const fetchCharacters = async () => {
      try {
        const response = await apiFetch(`/api/shows/${show._id}/characters`);
        if (!response.ok) throw new Error("Failed to fetch characters");
        const data = await response.json();
        setCharacters(
          data
            .filter((c) => c.voice_ids?.length > 0)
            .map((c) => ({ name: c.name, voiceId: c.voice_ids[0].voice_id })),
        );
      } catch (error) {
        console.error("Error fetching characters:", error);
      }
    };
    fetchCharacters();
  }, [hasCharactersInput, show?._id]);

  const updateModelData = (key, value) => {
    onModelDataChange((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Checks if a value is considered "filled" for a given input type.
   * @param {*} value - The value to check
   * @param {string} type - Input type: 'text' | 'options' | 'image' | 'video' | 'audio' | 'elements' | 'video_elements' | 'boolean'
   * @returns {boolean}
   */
  const isValueFilled = (value, type) => {
    if (value === null || value === undefined) return false;
    switch (type) {
      case "text":
      case "options":
        return String(value).trim() !== "";
      case "image":
      case "video":
      case "audio":
        return String(value).trim() !== "";
      case "elements":
        return (
          Array.isArray(value) &&
          value.length > 0 &&
          value.every((url) => url != null && String(url).trim() !== "")
        );
      case "video_elements":
        return (
          Array.isArray(value) &&
          value.length > 0 &&
          value.every((url) => url != null && String(url).trim() !== "")
        );
      case "audio_elements":
        return (
          Array.isArray(value) &&
          value.length > 0 &&
          value.every((url) => url != null && String(url).trim() !== "")
        );
      case "boolean":
        return typeof value === "boolean";
      case "character":
        return String(value).trim() !== "";
      case "voice_settings":
        return value != null && typeof value === "object";
      default:
        return true;
    }
  };

  const requiredFieldsFilled = (
    data = modelData,
    inputs = selectedModel?.inputs,
  ) => {
    if (!inputs?.length) return true;
    const requiredInputs = inputs.filter((input) => input.required === true);
    if (!requiredInputs.length) return true;
    return requiredInputs.every((input) =>
      isValueFilled(data?.[input.prop], input.type),
    );
  };

  const handleAddAsset = async (url) => {
    onModelDataChange((prev) => ({ ...prev, assets: [...prev.assets, url] }));
  };

  const handleRemoveAsset = (url) => {
    onModelDataChange((prev) => ({
      ...prev,
      assets: prev.assets.filter((a) => a !== url),
    }));
  };

  const resolvePreviewUrl = async () => {
    if (!preview?.url) return null;
    return typeof preview.url === "function"
      ? await preview.url()
      : preview.url;
  };

  const handleReplaceAsset = async (asset) => {
    const url = await resolvePreviewUrl();
    if (!url) return;
    onModelDataChange((prev) => ({
      ...prev,
      assets: prev.assets.map((a) => (a === asset.url ? url : a)),
    }));
  };

  const handleChatKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onSubmit?.({ totalGenerations, run_at: null });
    }
    onKeyDown?.(e);
  };

  const handleSubmit = async (schedule = false) => {
    let run_at = null;

    if (schedule) {
      const scheduledDateTime = await openModal(<ScheduleModal />);
      if (!scheduledDateTime) return;
      run_at = scheduledDateTime;
    }

    await onSubmit?.({ totalGenerations, run_at });
  };

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      <div className="grid grid-cols-2 gap-2 max-h-full overflow-y-auto pr-3 pb-2">
        {selectedModel?.inputs
          ?.filter((input) => input.type)
          .map((input, index) => (
            <div
              key={index}
              className={`flex flex-col gap-1 ${input.type === "options" || input.type === "boolean" || input.type === "character" || input.type === "voice_settings" || input.type === "number" ? "" : "col-span-2"}`}>
              <div className="text-[10px] text-secondary-foreground/80 px-1 select-none flex gap-1">
                <span>{input.name}</span>
                {input.required && (
                  <span className="text-destructive-foreground text-xs">*</span>
                )}
              </div>

              {input.type === "options" && (
                <Select
                  value={modelData?.[input.prop]}
                  onValueChange={(e) => updateModelData(input.prop, e)}>
                  <SelectTrigger className="text-xs select-none min-w-20 w-full">
                    <SelectValue placeholder={input.name} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {input.enum?.map((option) => (
                        <SelectItem
                          key={option.key}
                          value={option.key}
                          className="text-xs">
                          {option.value}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              )}

              {input.type === "character" && (
                <Select
                  value={modelData?.[input.prop]}
                  onValueChange={(e) => updateModelData(input.prop, e)}>
                  <SelectTrigger className="text-xs select-none min-w-20 w-full">
                    <SelectValue placeholder="Select a character" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {characters.map((c) => (
                        <SelectItem
                          key={c.voiceId}
                          value={c.voiceId}
                          className="text-xs">
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              )}

              {input.type === "number" && (
                <Input
                  type="number"
                  min={input.min ?? -9999}
                  max={input.max ?? 9999}
                  step={input.step ?? 1}
                  value={modelData?.[input.prop] ?? ""}
                  onChange={(e) =>
                    updateModelData(input.prop, parseFloat(e.target.value ?? 0))
                  }
                />
              )}

              {(input.type === "elements" ||
                input.type === "video_elements" ||
                input.type === "audio_elements") && (
                <div className="flex flex-col gap-1">
                  {modelData?.[input.prop]?.map((url, index, arr) => (
                    <AssetPicker
                      preview={preview}
                      key={`${index}-${url}`}
                      mediaType={
                        input.type === "elements"
                          ? "image"
                          : input.type === "video_elements"
                            ? "video"
                            : "audio"
                      }
                      asset={url}
                      onSelect={onPreviewChange}
                      onChange={(newUrl) =>
                        onModelDataChange((prev) => ({
                          ...prev,
                          [input.prop]: newUrl
                            ? prev[input.prop].map((u) =>
                                u === url ? newUrl : u,
                              )
                            : prev[input.prop].filter((u) => u !== url),
                        }))
                      }
                      onRemove={() =>
                        onModelDataChange((prev) => ({
                          ...prev,
                          [input.prop]: prev[input.prop].filter(
                            (u) => u !== url,
                          ),
                        }))
                      }
                      onReplace={async (asset) => {
                        const resolved = await resolvePreviewUrl();
                        if (resolved)
                          onModelDataChange((prev) => ({
                            ...prev,
                            [input.prop]: prev[input.prop].map((u) =>
                              u === asset.url ? resolved : u,
                            ),
                          }));
                      }}
                      canReplace={
                        (typeof preview?.url === "function" ||
                          url !== preview?.url) &&
                        preview?.type === "image"
                      }
                      onMoveUp={
                        index > 0
                          ? () =>
                              onModelDataChange((prev) => {
                                const next = [...prev[input.prop]];
                                [next[index - 1], next[index]] = [
                                  next[index],
                                  next[index - 1],
                                ];
                                return { ...prev, [input.prop]: next };
                              })
                          : null
                      }
                      onMoveDown={
                        index < arr.length - 1
                          ? () =>
                              onModelDataChange((prev) => {
                                const next = [...prev[input.prop]];
                                [next[index], next[index + 1]] = [
                                  next[index + 1],
                                  next[index],
                                ];
                                return { ...prev, [input.prop]: next };
                              })
                          : null
                      }
                    />
                  ))}
                  {modelData?.[input.prop]?.length < (input.max || 8) && (
                    <AssetPicker
                      preview={preview}
                      mediaType={
                        input.type === "elements"
                          ? "image"
                          : input.type === "video_elements"
                            ? "video"
                            : "audio"
                      }
                      onAdd={(assets) =>
                        onModelDataChange((prev) => ({
                          ...prev,
                          [input.prop]: [
                            ...prev[input.prop],
                            ...(Array.isArray(assets)
                              ? assets.map((a) => a.url)
                              : [assets]),
                          ].slice(0, input.max || 8),
                        }))
                      }
                      allowPreview={false}
                      multiSelect={true}
                    />
                  )}
                </div>
              )}

              {(input.type === "image" ||
                input.type === "video" ||
                input.type === "audio") && (
                <AssetPicker
                  preview={preview}
                  mediaType={input.type}
                  asset={modelData?.[input.prop]}
                  onSelect={onPreviewChange}
                  onChange={(url) =>
                    onModelDataChange((prev) => ({
                      ...prev,
                      [input.prop]: url,
                    }))
                  }
                  onRemove={() =>
                    onModelDataChange((prev) => ({
                      ...prev,
                      [input.prop]: null,
                    }))
                  }
                  onReplace={async () => {
                    const resolved = await resolvePreviewUrl();
                    if (resolved)
                      onModelDataChange((prev) => ({
                        ...prev,
                        [input.prop]: resolved,
                      }));
                  }}
                  canReplace={
                    (typeof preview?.url === "function" ||
                      modelData?.[input.prop] !== preview?.url) &&
                    preview?.type === input.type
                  }
                />
              )}

              {input.type === "boolean" && (
                <Switch
                  checked={modelData?.[input.prop]}
                  onCheckedChange={(checked) =>
                    updateModelData(input.prop, checked)
                  }
                />
              )}

              {input.type === "voice_settings" && (
                <Button
                  type="secondary"
                  size="sm"
                  icon={<FiSettings className="w-3 h-3" />}
                  className="h-full"
                  onClick={async () => {
                    const settings = await openModal(
                      <VoiceSettingsModal
                        voiceSettings={modelData?.[input.prop] || {}}
                      />,
                    );
                    if (settings) updateModelData(input.prop, settings);
                  }}>
                  {modelData?.[input.prop] ? "Edit Settings" : "Voice Settings"}
                </Button>
              )}

              {input.type === "text" && (
                <div className="flex items-center border border-secondary bg-input w-full rounded-lg relative">
                  <textarea
                    value={modelData?.[input.prop] ?? ""}
                    onChange={(e) => {
                      updateModelData(input.prop, e.target.value);
                    }}
                    placeholder={input.name}
                    className="placeholder:text-secondary-foreground/30 flex-1 text-xs font-mono p-2 pr-5 h-auto min-h-28 bg-transparent border-none outline-hidden resize-y overflow-y-auto"
                    rows={6}
                  />
                  {/* {modelData?.[input.prop] && <div className='absolute top-0 right-0 flex items-center justify-center text-secondary-foreground/80 p-2 hover:text-secondary-foreground cursor-pointer' onClick={()=>{updateModelData(input.prop, '')}}>
                                <FiX className='w-4 h-4' />
                            </div>} */}
                </div>
              )}
            </div>
          ))}
      </div>

      <div className="flex items-end col-span-2 pr-2">
        {selectedModel?.max_generations > 1 && (
          <div className="flex flex-col select-none px-1">
            <label className="text-[10px] text-secondary-foreground/80 select-none">
              Generations
            </label>
            <Select
              value={totalGenerations}
              onValueChange={setTotalGenerations}>
              <SelectTrigger className="text-xs select-none min-w-16 rounded-lg border-0">
                <SelectValue placeholder="Generations" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {Array.from({ length: 4 }, (_, i) => i + 1).map((i) => (
                    <SelectItem key={i} value={i} className="text-xs">
                      {i}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        )}
        <Button
          type="primary"
          className="rounded-r-none border-0 flex-1 h-9"
          onClick={() => handleSubmit(false)}
          disabled={!requiredFieldsFilled()}>
          Generate
        </Button>
        <Button
          type="primary"
          className="rounded-l-none border- h-9 border-0"
          onClick={() => handleSubmit(true)}
          disabled={!requiredFieldsFilled()}>
          <FiCalendar className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
